import './ConnectionLostModal.css';

import { IonIcon, IonRow } from '@ionic/react';
import { checkmarkCircleOutline, closeCircleOutline, gameControllerOutline } from 'ionicons/icons';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { BleService } from 'src/ble/BleService';
import { useBle } from 'src/ble/useBle';
import { DqText } from 'src/components/dq-text';
import { ControllerMessages } from 'src/intl/ControllerMessages';
import { bleActions } from 'src/redux/ble/actions';
import { BleConnectionState, BleUpdateFirmwareState } from 'src/redux/ble/constants';
import { GenericMessages } from '../../intl/GenericMessages';
import { AppModal, AppModalActionButton } from '../reusable-components/app-modal/AppModal';
import { selectBleIsConnectControllerModalOpen, selectBleIsLocked } from 'src/redux/ble/selectors';

interface ConnectionStatusInfo {
  title: string;
  icon: any;
  header: string;
  description: string;
  primaryButton: AppModalActionButton;
  secondaryButton?: AppModalActionButton;
}

type ConnectionStatus =
  | 'connectionLost'
  | 'firmwareInstallSuccess'
  | 'firmwareInstallFailure'
  | 'firmwareFetchingFailure'
  | 'firmwareLicensingFailure';

export const ConnectionStatusInfoModal: React.FC = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { lastConnectedDevice, bleState, updateFirmwareStatus } = useBle();
  const isBleLocked = useSelector(selectBleIsLocked);
  const isConnectControllerModalOpen = useSelector(selectBleIsConnectControllerModalOpen);

  const isOpen =
    (bleState !== BleConnectionState.CONNECTED && !!lastConnectedDevice && !isConnectControllerModalOpen) || // Connection Lost
    updateFirmwareStatus === BleUpdateFirmwareState.SUCCESS || // Firmware install success
    updateFirmwareStatus === BleUpdateFirmwareState.FAILURE_INSTALLING || // Firmware install failed
    updateFirmwareStatus === BleUpdateFirmwareState.FAILURE_FETCHING || // Firmware fetching failed
    updateFirmwareStatus === BleUpdateFirmwareState.FAILURE_LICENSING; // Firmware licensing failed

  const connectionStatus: ConnectionStatus = React.useMemo(() => {
    if (updateFirmwareStatus === BleUpdateFirmwareState.SUCCESS) {
      return 'firmwareInstallSuccess';
    }
    if (updateFirmwareStatus === BleUpdateFirmwareState.FAILURE_INSTALLING) {
      return 'firmwareInstallFailure';
    }
    if (updateFirmwareStatus === BleUpdateFirmwareState.FAILURE_FETCHING) {
      return 'firmwareFetchingFailure';
    }
    if (updateFirmwareStatus === BleUpdateFirmwareState.FAILURE_LICENSING) {
      return 'firmwareLicensingFailure';
    }
    return 'connectionLost';
  }, [updateFirmwareStatus]);

  const cancelConnectionAttempt = React.useCallback(() => {
    // if we are connected after connection was lost, we don't want to clear the state: the state is already restored
    // without this, we clear the lastConnectedDevice value, so we never get the reconnect prompt again!
    if (connectionStatus !== 'connectionLost' || bleState !== BleConnectionState.CONNECTED) {
      dispatch(bleActions.acknowledgeAndClearErrors());
    }
  }, [dispatch, connectionStatus, bleState]);

  const reconnect = React.useCallback(async () => {
    if (lastConnectedDevice) {
      try {
        await BleService.connect(lastConnectedDevice);
        await BleService.controller?.listBundles();
      } catch (err) {
        console.error('Error connecting to BLE device.', err);
        dispatch(bleActions.openConnectControllerModal());
        dispatch(bleActions.acknowledgeAndClearErrors());
      }
    }
  }, [dispatch, lastConnectedDevice]);

  const connectionLostButton = React.useMemo(() => {
    return {
      label: formatMessage(ControllerMessages.connectionLostButtonRetry),
      onClick: reconnect,
      disabled: isBleLocked,
    };
  }, [formatMessage, isBleLocked, reconnect]);

  const cancelButton = React.useMemo(() => {
    return {
      label: formatMessage(ControllerMessages.connectionLostButtonCancel),
      onClick: cancelConnectionAttempt,
    };
  }, [formatMessage, cancelConnectionAttempt]);

  const closeButton = React.useMemo(() => {
    return {
      label: formatMessage(GenericMessages.closeButtonPlaceholder),
      onClick: cancelConnectionAttempt,
    };
  }, [cancelConnectionAttempt, formatMessage]);

  const modalInfo: ConnectionStatusInfo = React.useMemo(() => {
    switch (connectionStatus) {
      case 'connectionLost':
        return {
          title: formatMessage(ControllerMessages.connectionLostHeader),
          icon: gameControllerOutline,
          header: formatMessage(ControllerMessages.connectionLostHeader),
          description: formatMessage(ControllerMessages.connectionLostInstructions),
          primaryButton: connectionLostButton,
          secondaryButton: cancelButton,
        };
      case 'firmwareInstallSuccess':
        return {
          title: formatMessage(ControllerMessages.firmwareVersionUpdateSuccess),
          icon: checkmarkCircleOutline,
          header: formatMessage(ControllerMessages.firmwareVersionUpdateSuccess),
          description: formatMessage(ControllerMessages.firmwareVersionUpdateDetails),
          primaryButton: cancelButton,
          secondaryButton: undefined,
        };
      case 'firmwareInstallFailure':
        return {
          title: formatMessage(ControllerMessages.firmwareInstallFailure),
          icon: closeCircleOutline,
          header: formatMessage(ControllerMessages.firmwareInstallFailure),
          description: formatMessage(ControllerMessages.firmwareInstallFailureDetails),
          primaryButton: closeButton,
          secondaryButton: undefined,
        };
      case 'firmwareFetchingFailure':
        return {
          title: formatMessage(ControllerMessages.firmwareInstallFailure),
          icon: closeCircleOutline,
          header: formatMessage(ControllerMessages.firmwareInstallFailure),
          description: formatMessage(ControllerMessages.firmwareFetchingFailureDetails),
          primaryButton: closeButton,
          secondaryButton: undefined,
        };
      case 'firmwareLicensingFailure':
        return {
          title: formatMessage(ControllerMessages.firmwareInstallFailure),
          icon: closeCircleOutline,
          header: formatMessage(ControllerMessages.firmwareInstallFailure),
          description: formatMessage(ControllerMessages.firmwareLicensingFailureDetails),
          primaryButton: closeButton,
          secondaryButton: undefined,
        };
    }
  }, [cancelButton, closeButton, connectionLostButton, connectionStatus, formatMessage]);

  return (
    <AppModal
      isOpen={isOpen}
      title={modalInfo.title}
      onClose={cancelConnectionAttempt}
      primaryButton={modalInfo.primaryButton}
      secondaryButton={modalInfo.secondaryButton}
      mobileSheetBreakpoints={[0, 1]}
      mobileSheetInitialBreakpoint={1}
    >
      <div className="container">
        <div>
          <IonIcon slot="start" className="controller" icon={modalInfo.icon} />
        </div>
        <IonRow className="directions">
          <DqText variant="h3">{modalInfo.header}</DqText>
        </IonRow>
        <IonRow className="directions">
          <DqText variant="body">{modalInfo.description}</DqText>
        </IonRow>
        <div className="button-container" />
      </div>
    </AppModal>
  );
};
